import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, CompanyCrmLists, CompanyService } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

/**
 * Segment and company-size lists the lead form offers (E2 RF-09 · plan AD-11).
 *
 * Routed page of the CRM group, next to the lead-quality criteria and under the same
 * admin guard. The platform default is what the company sees until it saves its own.
 */
@Component({
  selector: 'crm-lists-config-component',
  templateUrl: './crm-lists-config.component.html',
  styleUrls: ['./crm-lists-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CrmListsConfigComponent extends AbstractComponent implements OnInit {

  lists: CompanyCrmLists = { segments: [], employeeBands: [] };
  override loading = true;
  saving = false;

  constructor(injector: Injector, private readonly companyService: CompanyService) {
    super(injector);
  }

  ngOnInit(): void {
    this.companyService
      .getCrmLists()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lists) => {
          this.lists = { segments: [...(lists?.segments ?? [])], employeeBands: [...(lists?.employeeBands ?? [])] };
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.alertService.error(this.t('kanban.crmLists.loadError'));
        },
      });
  }

  add(list: keyof CompanyCrmLists, input: HTMLInputElement): void {
    const value = input.value?.trim();
    if (!value) return;
    // The board sends a filter's values joined by commas: one holding a comma could never be selected.
    if (value.includes(',')) {
      this.alertService.warning(this.t('kanban.crmLists.commaNotAllowed'));
      return;
    }
    if (!this.lists[list].includes(value)) this.lists[list] = [...this.lists[list], value];
    input.value = '';
  }

  remove(list: keyof CompanyCrmLists, value: string): void {
    this.lists[list] = this.lists[list].filter((item) => item !== value);
  }

  save(): void {
    if (this.saving) return;
    this.saving = true;

    this.companyService
      .updateCrmLists(this.lists)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lists) => {
          this.lists = lists;
          this.saving = false;
          this.alertService.success(this.t('kanban.crmLists.saved'));
        },
        error: (error) => {
          this.saving = false;
          this.alertService.error(error?.error?.message ?? this.t('kanban.crmLists.saveError'));
        },
      });
  }
}
